import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import './_navbars.scss';
import CircleUserIcon from '../../assets/icons/bottom-tabs/circle-user.svg?react';
import CircleUserIconFilled from '../../assets/icons/bottom-tabs/circle-user-filled.svg?react';
import ClaimsIcon from '../../assets/icons/bottom-tabs/claims.svg?react';
import ClaimsIconFilled from '../../assets/icons/bottom-tabs/claims-filled.svg?react';
import HomeIcon from '../../assets/icons/bottom-tabs/home.svg?react';
import HomeIconFilled from '../../assets/icons/bottom-tabs/home-filled.svg?react';
import UsersIcon from '../../assets/icons/bottom-tabs/users.svg?react';
import UsersIconFilled from '../../assets/icons/bottom-tabs/users-filled.svg?react';

// Minimum scroll movement (px) before the pill reacts, and the offset from the top under which it stays expanded.
const SCROLL_DELTA = 8,
  TOP_THRESHOLD = 24,
  EMBEDDED_APP_PATH = '/gift-cards',
  // Expanded pill width per tab, plus the pill's horizontal padding.
  ITEM_WIDTH = 76,
  NAV_PADDING = 8;

const NavbarFooter: React.FC = () => {
  const activeMode = useSelector((state: any) => state.auth.activeMode),
    navbarLinks =
      activeMode === 'employer'
        ? [
          {
            id: 1,
            name: 'Home',
            icon: (isActive: boolean) => isActive ? <HomeIconFilled /> : <HomeIcon />,
            navigate: '/dashboard',
          },
          {
            id: 2,
            name: 'Members',
            icon: (isActive: boolean) => isActive ? <UsersIconFilled /> : <UsersIcon />,
            navigate: '/members',
          },
          {
            id: 3,
            name: 'Claims',
            icon: (isActive: boolean) => isActive ? <ClaimsIconFilled /> : <ClaimsIcon />,
            navigate: '/claims',
          },
          {
            id: 4,
            name: 'Profile',
            icon: (isActive: boolean) => isActive ? <CircleUserIconFilled /> : <CircleUserIcon />,
            navigate: '/profile',
          },
        ]
        : [
          {
            id: 1,
            name: 'Home',
            icon: (isActive: boolean) => isActive ? <HomeIconFilled /> : <HomeIcon />,
            navigate: '/home',
          },
          {
            id: 2,
            name: 'Claims',
            icon: (isActive: boolean) => isActive ? <ClaimsIconFilled /> : <ClaimsIcon />,
            navigate: '/claims',
          },
          {
            id: 4,
            name: 'Profile',
            icon: (isActive: boolean) => isActive ? <CircleUserIconFilled /> : <CircleUserIcon />,
            navigate: '/profile',
          },
        ];

  const location = useLocation(),
    dockRef = useRef<HTMLDivElement>(null),
    [collapsed, setCollapsed] = useState(false),
    [dockWidth, setDockWidth] = useState<number>();

  // Track the dock's width in px: the collapse is driven by transforms, which need the pill's width as a plain number.
  useLayoutEffect(() => {
    const dock = dockRef.current;
    if (!dock) return;
    const measure = () => {
      const style = getComputedStyle(dock);
      setDockWidth(dock.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(dock);
    return () => observer.disconnect();
  }, []);

  // Collapse into the active tab's icon while scrolling down, expand again on scroll up or near the top.
  // Listens in the capture phase so scrolling inside any scroll container (not just the window) counts.
  useEffect(() => {
    const lastTops = new WeakMap<object, number>();
    const onScroll = (event: Event) => {
      const target = event.target === document ? document.scrollingElement : (event.target as Element);
      if (!target) return;
      // Clamp to the real scroll range: iOS rubber-band overscroll reports positions past either edge, and the
      // spring-back at the bottom would otherwise read as a scroll up and re-expand the pill.
      const maxTop = Math.max(0, target.scrollHeight - target.clientHeight),
        top = Math.min(Math.max(target.scrollTop, 0), maxTop),
        lastTop = lastTops.get(target),
        delta = top - (lastTop ?? top);
      // Ignore horizontal-only scrolls (carousels) and jitter below the threshold.
      if (lastTop !== undefined && (delta === 0 || (Math.abs(delta) < SCROLL_DELTA && top > TOP_THRESHOLD))) return;
      lastTops.set(target, top);
      if (top <= TOP_THRESHOLD || delta < 0) setCollapsed(false);
      else if (delta > 0) setCollapsed(true);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  // Hubble scrolls inside a cross-origin iframe, so its scrolling can't be seen here. Keep the pill collapsed on
  // that page, and collapse it again when the user goes back to interacting with the iframe (the window blurs).
  const isEmbeddedAppPage = location.pathname.startsWith(EMBEDDED_APP_PATH);

  useEffect(() => setCollapsed(isEmbeddedAppPage), [location.pathname, isEmbeddedAppPage]);

  useEffect(() => {
    if (!isEmbeddedAppPage) return;
    const collapse = () => setCollapsed(true);
    window.addEventListener('blur', collapse);
    return () => window.removeEventListener('blur', collapse);
  }, [isEmbeddedAppPage]);

  // On pages outside the tabs nothing is active, so the collapsed pill shows Home instead of an empty circle.
  const hasActiveLink = navbarLinks.some((item) => matchPath({ path: item.navigate, end: false }, location.pathname));

  // While collapsed, a tap anywhere on the pill only expands it — it must not navigate.
  const expandOnTap = (event: React.MouseEvent) => {
    if (!collapsed) return;
    event.preventDefault();
    event.stopPropagation();
    setCollapsed(false);
  };

  return (
    <div className="bottom-nav-dock" ref={dockRef}>
      <nav
        aria-label="Primary"
        className={`bottom-nav${collapsed ? ' bottom-nav--collapsed' : ''}`}
        style={
          {
            '--bottom-nav-items': navbarLinks.length,
            ...(dockWidth !== undefined && {
              '--bottom-nav-dock-width': `${dockWidth}px`,
              '--bottom-nav-width': `${Math.min(dockWidth, navbarLinks.length * ITEM_WIDTH + NAV_PADDING)}px`,
            }),
          } as React.CSSProperties
        }
        onClickCapture={expandOnTap}
      >
        {/* The pill's background, in three pieces so it can shrink into a circle using only transforms. */}
        <span className="bottom-nav-shape" aria-hidden="true">
          <span className="bottom-nav-shape-cap bottom-nav-shape-cap--start" />
          <span className="bottom-nav-shape-mid" />
          <span className="bottom-nav-shape-cap bottom-nav-shape-cap--end" />
        </span>
        {navbarLinks.map((item: any, index: number) => (
          <NavLink
            key={item.id}
            to={item.navigate}
            style={{ '--bottom-nav-index': index } as React.CSSProperties}
            className={({ isActive }) =>
              `bottom-nav-item${isActive ? ' active' : ''}${!hasActiveLink && item.name === 'Home' ? ' bottom-nav-item--fallback' : ''}`
            }
          >
            {({ isActive }) => (
              <>
                <span className="bottom-nav-item-icon">{item.icon(isActive)}</span>
                <span className="bottom-nav-item-text">{item.name}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default NavbarFooter;
